/**
 * ⚠️ AGENDA TYPES - GEANKERD
 * Deze types zijn de contractuele basis voor de AgendaPage.
 * Wijzigingen alleen na expliciete review.
 * 
 * @stable
 * @since 2026-01-21
 */

// ============================================
// CALENDAR EVENT TYPES
// ============================================

export type CalendarEventType = 
  | 'podcast' 
  | 'meeting' 
  | 'call' 
  | 'personal' 
  | 'planning' 
  | 'google'
  | 'outlook'
  | 'networking';

export type CalendarEventCategory = 
  | 'work' 
  | 'friends' 
  | 'family' 
  | 'activity' 
  | 'action';

export type CalendarEventDomain = 'work' | 'private';

export type CalendarEventStatus = 
  | 'scheduled' 
  | 'confirmed' 
  | 'cancelled' 
  | 'completed';

export interface CalendarEventRecurring {
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  endDate?: Date;
  excludedDates?: Date[];
}

export interface CalendarEventChangeRequest {
  id: string;
  fromUserId: string;
  newStartTime: Date;
  newEndTime: Date;
  reason: string;
  status: 'pending' | 'accepted' | 'declined';
}

export interface DoelstellingenMeta {
  actionId: string;
  goalId: string;
  milestoneId?: string;
}

export interface LinkedTaskMeta {
  listId: string;
  listName: string;
  balanceCategory?: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  type: CalendarEventType;
  category?: CalendarEventCategory;
  domain?: CalendarEventDomain;
  source?: string;
  participants: string[];
  participantNames: string[] | { [uid: string]: string };
  location?: string;
  meetingUrl?: string;
  status: CalendarEventStatus;
  createdBy: string;
  createdAt: Date;
  reminderEnabled?: boolean;
  reminderMinutesBefore?: number;
  reminderAt?: Date | null;
  reminderSent: boolean;
  color?: string;
  recurring?: CalendarEventRecurring;
  autoAcceptNetworking?: boolean;
  pendingChangeRequest?: CalendarEventChangeRequest;
  connectionId?: string;
  phase?: 'call' | 'video' | 'physical';
  lockedToConnectionFlow?: boolean;
  doelstellingenMeta?: DoelstellingenMeta;
  linkedTaskId?: string;
  linkedTaskMeta?: LinkedTaskMeta;
}

// ============================================
// AGENDA TASK TYPES
// ============================================

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'blocked';
export type BalanceCategory = 'work' | 'self' | 'family' | 'activity';

export interface AgendaTask {
  id: string;
  title: string;
  description?: string;
  listId: string;
  completed: boolean;
  completedAt?: Date | null;
  originalListId?: string;
  createdAt: Date;
  deadline?: Date;
  priority?: TaskPriority;
  status?: TaskStatus;
  // Time tracking
  activeTime?: number;
  timerStartedAt?: Date | null;
  isTimerRunning?: boolean;
  // Metadata
  balanceCategory?: BalanceCategory;
  isDoelstellingAction?: boolean;
  doelstellingGoalId?: string;
  doelstellingMilestoneId?: string;
}

export interface AgendaTaskList {
  id: string;
  name: string;
  userId?: string;
  isSystem?: boolean;
  balanceCategory?: BalanceCategory;
  createdAt?: Date;
}

// ============================================
// DRAG & DROP PAYLOAD TYPES (DEFENSIEF)
// ============================================

export const DRAG_TYPE_DOELSTELLINGEN = 'doelstellingen-action' as const;
export const DRAG_TYPE_AGENDA_TASK = 'agenda-task' as const;

export interface DoelstellingenDragPayload {
  type: typeof DRAG_TYPE_DOELSTELLINGEN;
  actionId: string;
  goalId: string;
  goalTitle: string;
  milestoneId?: string;
  milestoneTitle?: string;
  actionTitle: string;
}

export interface AgendaTaskDragPayload {
  type: typeof DRAG_TYPE_AGENDA_TASK;
  taskId: string;
  taskTitle: string;
  listId: string;
  listName: string;
  balanceCategory?: string;
}

export type DragPayload = DoelstellingenDragPayload | AgendaTaskDragPayload;

// ============================================
// DRAG PAYLOAD VALIDATORS (DEFENSIEF)
// ============================================

/**
 * Valideert of een payload een geldige Doelstellingen drag payload is.
 * Retourneert null bij ongeldige payload (defensief).
 */
export function validateDoelstellingenPayload(data: unknown): DoelstellingenDragPayload | null {
  if (!data || typeof data !== 'object') return null;
  
  const payload = data as Record<string, unknown>;
  
  if (payload.type !== DRAG_TYPE_DOELSTELLINGEN) return null;
  if (typeof payload.actionId !== 'string' || !payload.actionId) return null;
  if (typeof payload.goalId !== 'string' || !payload.goalId) return null;
  if (typeof payload.goalTitle !== 'string') return null;
  if (typeof payload.actionTitle !== 'string') return null;
  
  return {
    type: DRAG_TYPE_DOELSTELLINGEN,
    actionId: payload.actionId,
    goalId: payload.goalId,
    goalTitle: payload.goalTitle,
    milestoneId: typeof payload.milestoneId === 'string' ? payload.milestoneId : undefined,
    milestoneTitle: typeof payload.milestoneTitle === 'string' ? payload.milestoneTitle : undefined,
    actionTitle: payload.actionTitle,
  };
}

/**
 * Valideert of een payload een geldige Agenda Task drag payload is.
 * Retourneert null bij ongeldige payload (defensief).
 */
export function validateAgendaTaskPayload(data: unknown): AgendaTaskDragPayload | null {
  if (!data || typeof data !== 'object') return null;
  
  const payload = data as Record<string, unknown>;
  
  if (payload.type !== DRAG_TYPE_AGENDA_TASK) return null;
  if (typeof payload.taskId !== 'string' || !payload.taskId) return null;
  if (typeof payload.taskTitle !== 'string') return null;
  if (typeof payload.listId !== 'string') return null;
  if (typeof payload.listName !== 'string') return null;
  
  return {
    type: DRAG_TYPE_AGENDA_TASK,
    taskId: payload.taskId,
    taskTitle: payload.taskTitle,
    listId: payload.listId,
    listName: payload.listName,
    balanceCategory: typeof payload.balanceCategory === 'string' ? payload.balanceCategory : undefined,
  };
}

/**
 * Valideert een drag payload en retourneert het gevalideerde type.
 * Retourneert null bij onbekende of ongeldige payloads (defensief).
 * 
 * BELANGRIJK: Onbekende payloads worden GENEGEERD, niet verwerkt.
 */
export function validateDragPayload(data: unknown): DragPayload | null {
  // Probeer Doelstellingen payload
  const doelstellingenPayload = validateDoelstellingenPayload(data);
  if (doelstellingenPayload) return doelstellingenPayload;
  
  // Probeer Agenda Task payload
  const agendaTaskPayload = validateAgendaTaskPayload(data);
  if (agendaTaskPayload) return agendaTaskPayload;
  
  // Onbekende payload → null (wordt genegeerd)
  return null;
}

/**
 * Parse drag data van een DragEvent.
 * Retourneert null bij parse errors of ongeldige data.
 */
export function parseDragData(e: React.DragEvent): DragPayload | null {
  try {
    const rawData = e.dataTransfer.getData('text/plain');
    if (!rawData) return null;
    
    const parsed = JSON.parse(rawData);
    return validateDragPayload(parsed);
  } catch {
    // JSON parse error → null (defensief)
    return null;
  }
}

// ============================================
// VIEW STATE TYPES
// ============================================

export type CalendarViewMode = 'day' | 'week' | 'month';
export type DayViewTab = 'events' | 'tasks';
export type SidebarTab = 'tasks' | 'planning' | 'doelstellingen' | 'assistant' | 'admin';

export interface CalendarVisibility {
  superkonnected: boolean;
  google: boolean;
  outlook: boolean;
  planning: boolean;
  personal: boolean;
}

// ============================================
// HELPER TYPE GUARDS
// ============================================

export function isDoelstellingenPayload(payload: DragPayload): payload is DoelstellingenDragPayload {
  return payload.type === DRAG_TYPE_DOELSTELLINGEN;
}

export function isAgendaTaskPayload(payload: DragPayload): payload is AgendaTaskDragPayload {
  return payload.type === DRAG_TYPE_AGENDA_TASK;
}
